import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { offlineMutate, offlineQualifier } from "@/lib/offlineToast";
import { usePaymentHistory } from "@/hooks/usePaymentHistory";
import { type PaymentHistory } from "@/repositories/interfaces";
import { type Subscription } from "@/types/subscription";

interface AddTopUpModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subscription: Subscription;
  onCreated?: (payment: any) => void;
}

export const AddTopUpModal = ({
  open,
  onOpenChange,
  subscription,
  onCreated,
}: AddTopUpModalProps) => {
  const { createPayment } = usePaymentHistory();

  const [amount, setAmount] = useState<string>(
    subscription.billingType === "pay_as_you_go"
      ? ""
      : String(subscription.amount ?? ""),
  );
  const [currency, setCurrency] = useState<string>(
    subscription.currency || "EUR",
  );
  const [date, setDate] = useState<string>(
    new Date().toISOString().split("T")[0],
  );
  const [notes, setNotes] = useState<string>(
    subscription.billingType === "pay_as_you_go" ? "Top-up" : "",
  );
  const [reconciliationSource, setReconciliationSource] =
    useState<PaymentHistory["reconciliation_source"]>("manual");

  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = () => {
    setAmount(
      subscription.billingType === "pay_as_you_go"
        ? ""
        : String(subscription.amount ?? ""),
    );
    setCurrency(subscription.currency || "EUR");
    setDate(new Date().toISOString().split("T")[0]);
    setNotes(subscription.billingType === "pay_as_you_go" ? "Top-up" : "");
    setReconciliationSource("manual");
  };

  const validate = () => {
    if (!amount || Number(amount) <= 0) {
      toast.success("Invalid amount", { description: "Amount must be greater than 0." });
      return false;
    }
    if (!date) {
      toast.success("Invalid date", { description: "Please pick a payment date." });
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const payload: Omit<PaymentHistory, "id" | "created_at"> = {
        subscription_id: subscription.id,
        payment_date: date,
        amount: Number(amount),
        currency,
        status: "paid" as PaymentHistory["status"],
        reconciliation_source:
          reconciliationSource as PaymentHistory["reconciliation_source"],
        notes: notes || "Top-up",
      };
      const result = await offlineMutate(createPayment, payload);
      toast.success("Top-up added", { description: offlineQualifier("Top-up recorded successfully.") });
      resetForm();
      onOpenChange(false);
      if (result) onCreated?.(result);
    } catch (error) {
      console.error("Failed to create top-up:", error);
      toast.success("Failed to add top-up", { description: "An error occurred while recording the top-up." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[480px]">
        <DialogHeader>
          <DialogTitle>
            {subscription.billingType === "pay_as_you_go"
              ? `Add Top-up for ${subscription.name}`
              : `Record Payment for ${subscription.name}`}
          </DialogTitle>
          <DialogDescription>
            {subscription.billingType === "pay_as_you_go"
              ? "Record a one-off top-up / pay-as-you-go payment for this subscription."
              : "Record a manual payment for this subscription."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label>Amount</Label>
            <Input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 10.00"
              type="number"
              step="0.01"
            />
          </div>

          <div>
            <Label>Currency</Label>
            <Select value={currency} onValueChange={(v) => setCurrency(v)}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="EUR">EUR</SelectItem>
                <SelectItem value="GBP">GBP</SelectItem>
                <SelectItem value="USD">USD</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Date</Label>
            <Input
              value={date}
              onChange={(e) => setDate(e.target.value)}
              type="date"
            />
          </div>

          <div>
            <Label>Reconciliation source</Label>
            <Select
              value={reconciliationSource}
              onValueChange={(v) =>
                setReconciliationSource(
                  v as PaymentHistory["reconciliation_source"],
                )
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="manual">Manual</SelectItem>
                <SelectItem value="imported">Imported (bank/file)</SelectItem>
                <SelectItem value="ynab">YNAB</SelectItem>
                <SelectItem value="bank">Bank</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Notes</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes"
            />
          </div>

          <div className="flex justify-end space-x-2 pt-2">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Add top-up"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
